import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../helpers/axios';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

// Fixed bucket order — always displayed as columns in the agent table
const BUCKET_ORDER = ['S1', 'S2', 'S3', 'M1', 'M2', 'X1', 'X2', 'WO'];
  
// Colour coding per bucket — premium fintech palette with visible contrast
const BUCKET_COLOR = {
  S1: { bg: '#BBF7D0', text: '#14532D', border: '#86EFAC' },
  S2: { bg: '#FDE68A', text: '#78350F', border: '#FCD34D' },
  S3: { bg: '#FED7AA', text: '#7C2D12', border: '#FDBA74' },
  M1: { bg: '#FECDD3', text: '#881337', border: '#FDA4AF' },
  M2: { bg: '#E9D5FF', text: '#4C1D95', border: '#D8B4FE' },
  X1: { bg: '#FCA5A5', text: '#7F1D1D', border: '#F87171' },
  X2: { bg: '#BFDBFE', text: '#1E3A8A', border: '#93C5FD' },
  WO: { bg: '#374151', text: '#F9FAFB', border: '#6B7280' },
};

// Format a number as Indian Rupees — e.g. 47615 → ₹47,615
function formatINR(amount) {
  const num = Number(amount) || 0;
  return '₹' + num.toLocaleString('en-IN');
}

export default function ManagerAllocation({ initialData }) {

  // ── Page-level data ──────────────────────────────────────────────────────────
  const [agents, setAgents]                   = useState(initialData?.agentsRaw?.agents || []);
  const [rotationHistory, setRotationHistory] = useState(initialData?.rotationHistoryRaw?.records || []);
  const [allocationMethod, setAllocationMethod] = useState('—');
  const [loading, setLoading]                 = useState(!initialData);

  // ── Modal open/close ─────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);

  // ── Modal Step 1: bucket ─────────────────────────────────────────────────────
  const [selectedBucket, setSelectedBucket] = useState('');

  // ── Modal Step 2: loans list + multi-selection ───────────────────────────────
  const [overdueLoans, setOverdueLoans] = useState([]);
  const [loansLoading, setLoansLoading] = useState(false);
  // selectedLoanIds is an ARRAY — supports selecting one or many loans at once
  // e.g. ['MCLMNP2500005', 'MCLMNP2500004']
  const [selectedLoanIds, setSelectedLoanIds] = useState([]);

  // ── Modal Step 3: agent ──────────────────────────────────────────────────────
  const [selectedAgentId, setSelectedAgentId] = useState('');

  // ── Modal Step 4: reason (optional) ─────────────────────────────────────────
  const [allocateReason, setAllocateReason] = useState('');

  // ── Submission progress ──────────────────────────────────────────────────────
  // When allocating multiple loans, we call the API once per loan.
  // allocateProgress tracks how many are done so we can show "Allocating 2/5..."
  const [allocating, setAllocating] = useState(false);
  const [allocateProgress, setAllocateProgress] = useState({ done: 0, total: 0 });

  // ── Agent loans peek + reassign ──────────────────────────────────────────────
  const [peekAgent, setPeekAgent] = useState(null); // { agentId, agentName }
  const [agentLoans, setAgentLoans] = useState([]);
  const [agentLoansLoading, setAgentLoansLoading] = useState(false);
  const [reassignLoanId, setReassignLoanId] = useState(null);
  const [reassignTargetId, setReassignTargetId] = useState('');
  const [reassignReason, setReassignReason] = useState('');
  const [reassigning, setReassigning] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingAgentId, setExportingAgentId] = useState(null);

  // ── Agent loans modal: filter / sort / pagination ────────────────────────────
  const [modalBucket, setModalBucket] = useState('ALL');
  const [modalSearch, setModalSearch] = useState('');
  const [modalPage, setModalPage] = useState(1);
  const [modalSort, setModalSort] = useState({ col: null, asc: true });
  const [agentLoansGrandTotals, setAgentLoansGrandTotals] = useState(null);
  const [agentBucketCounts, setAgentBucketCounts] = useState({});

  // ── Load page data on mount ──────────────────────────────────────────────────
  useEffect(() => {
    // Always fetch settings (allocation method); skip agents+history if parent provided them
    const fetchSettings = axiosInstance.get('/collection-incentive/settings')
      .then((res) => setAllocationMethod(res.data.data.allocation?.method || '—'))
      .catch(() => {});

    if (initialData) {
      fetchSettings.finally(() => setLoading(false));
      return;
    }

    Promise.all([
      axiosInstance.get('/collection-incentive/manager/agents'),
      axiosInstance.get('/collection-incentive/manager/rotation-history'),
      fetchSettings,
    ])
      .then(([agentsRes, historyRes]) => {
        setAgents(agentsRes.data.data.agents || []);
        setRotationHistory(historyRes.data.data.records || []);
      })
      .catch((error) => {
        alert(error?.response?.data?.message || 'Failed to load allocation data');
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!initialData) return;
    setAgents(initialData.agentsRaw?.agents || []);
    setRotationHistory(initialData.rotationHistoryRaw?.records || []);
    setLoading(false);
  }, [initialData]);

  const lastRotation = rotationHistory[0];

  // Per-bucket totals across all agents — denominator for heatmap fill bars
  const bucketTotals = BUCKET_ORDER.reduce((acc, b) => {
    acc[b] = agents.reduce((sum, a) => sum + (a.bucketDistribution?.[b] ?? 0), 0);
    return acc;
  }, {});

  // ── Export CSV ───────────────────────────────────────────────────────────────

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axiosInstance.get('/collection-incentive/manager/export');
      const rows = res.data.data || [];
      if (rows.length === 0) {
        alert('No active allocations to export.');
        return;
      }

      const cols = ['Agent', 'Loan ID', 'Legacy Loan ID', 'Borrower Name', 'Phone', 'Bucket', 'DPD', 'Overdue Amount (INR)', 'Allocated On'];
      const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const lines = [
        cols.join(','),
        ...rows.map((r) => [
          esc(r.agentName),
          esc(r.loanId),
          esc(r.legacyLoanId),
          esc(r.borrowerName),
          esc(r.phone),
          esc(r.bucket),
          esc(r.dpd),
          esc(r.totalOverdue),
          esc(r.allocatedAt ? new Date(r.allocatedAt).toLocaleDateString('en-IN') : ''),
        ].join(',')),
      ];

      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const today = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `allocation-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e?.response?.data?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // ── Per-agent CSV export ─────────────────────────────────────────────────────
  // Calls the existing loans endpoint, converts response to CSV, triggers download.
  // exportingAgentId tracks which row's button is in loading state.

  const handleExportAgent = async (agent) => {
    setExportingAgentId(agent.agentId);
    try {
      const res = await axiosInstance.get(`/collection-incentive/manager/agents/${agent.agentId}/loans`);
      const loans = res.data.data?.data || res.data.data || [];
      if (loans.length === 0) {
        alert('No loans assigned to this agent.');
        return;
      }

      const cols = ['Loan ID', 'Legacy Loan ID', 'Borrower Name', 'Phone', 'Bucket', 'DPD', 'Overdue Amount (INR)', 'POS (INR)', 'Total Collected (INR)', 'Allocated Date'];
      const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const lines = [
        cols.join(','),
        ...loans.map((l) => [
          esc(l.loanId),
          esc(l.legacyLoanId),
          esc(l.borrowerName),
          esc(l.phone),
          esc(l.bucket),
          esc(l.dpd),
          esc(l.totalOverdue),
          esc(l.pos),
          esc(l.totalCollected),
          esc(l.allocatedAt ? new Date(l.allocatedAt).toLocaleDateString('en-IN') : ''),
        ].join(',')),
      ];

      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const today = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `${agent.agentName}-loans-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e?.response?.data?.message || 'Export failed');
    } finally {
      setExportingAgentId(null);
    }
  };

  // ── Agent loans peek handlers ────────────────────────────────────────────────

  const openAgentLoans = (agent) => {
    setPeekAgent(agent);
    setAgentLoans([]);
    setAgentLoansGrandTotals(null);
    setAgentBucketCounts({});
    setReassignLoanId(null);
    setReassignTargetId('');
    setReassignReason('');
    setModalBucket('ALL');
    setModalSearch('');
    setModalPage(1);
    setModalSort({ col: null, asc: true });
    setAgentLoansLoading(true);
    axiosInstance
      .get(`/collection-incentive/manager/agents/${agent.agentId}/loans?pageNo=1&perPage=15`)
      .then((res) => {
        const result = res.data.data;
        setAgentLoans(result.data || result || []);
        if (result.totals) setAgentLoansGrandTotals(result.totals);
        if (result.bucketCounts) setAgentBucketCounts(result.bucketCounts);
      })
      .catch((e) => alert(e?.response?.data?.message || 'Failed to load loans'))
      .finally(() => setAgentLoansLoading(false));
  };

  const fetchModalLoans = (agentId, page, bucket) => {
    setAgentLoansLoading(true);
    const bucketParam = bucket && bucket !== 'ALL' ? `&bucket=${bucket}` : '';
    axiosInstance
      .get(`/collection-incentive/manager/agents/${agentId}/loans?pageNo=${page}&perPage=15${bucketParam}`)
      .then((res) => {
        const result = res.data.data;
        setAgentLoans(result.data || result || []);
      })
      .catch((e) => alert(e?.response?.data?.message || 'Failed to load loans'))
      .finally(() => setAgentLoansLoading(false));
  };

  const closeAgentLoans = () => {
    setPeekAgent(null);
    setAgentLoans([]);
    setAgentLoansGrandTotals(null);
    setAgentBucketCounts({});
    setReassignLoanId(null);
    setReassignTargetId('');
    setReassignReason('');
    setModalBucket('ALL');
    setModalSearch('');
    setModalPage(1);
  };

  const handleReassign = async () => {
    if (!reassignTargetId) return alert('Please select a target agent');
    setReassigning(true);
    try {
      await axiosInstance.post(
        '/collection-incentive/manager/reassign',
        { loanIds: [reassignLoanId], targetAgentId: reassignTargetId, reason: reassignReason.trim() || undefined }
      );
      alert('Loan reassigned successfully');
      const [loansRes, agentsRes] = await Promise.all([
        axiosInstance.get(`/collection-incentive/manager/agents/${peekAgent.agentId}/loans?pageNo=${modalPage}&perPage=15`),
        axiosInstance.get('/collection-incentive/manager/agents'),
      ]);
      const loansResult = loansRes.data.data;
      setAgentLoans(loansResult.data || loansResult || []);
      if (loansResult.totals) setAgentLoansGrandTotals(loansResult.totals);
      if (loansResult.bucketCounts) setAgentBucketCounts(loansResult.bucketCounts);
      setAgents(agentsRes.data.data.agents || []);
      setReassignLoanId(null);
      setReassignTargetId('');
      setReassignReason('');
    } catch (e) {
      alert(e?.response?.data?.message || 'Reassign failed');
    }
    setReassigning(false);
  };

  // ── Multi-select helpers ─────────────────────────────────────────────────────

  // Toggle a single loan row: if already selected → remove it, else → add it
  const toggleLoan = (loanId) => {
    setSelectedLoanIds((prev) =>
      prev.includes(loanId)
        ? prev.filter((id) => id !== loanId)
        : [...prev, loanId]
    );
  };

  // "Select All" checkbox in the table header.
  // If every loan is already selected → deselect all. Otherwise → select all.
  const toggleSelectAll = () => {
    const allIds = overdueLoans.map((loan) => loan.loanId);
    const allSelected = allIds.every((id) => selectedLoanIds.includes(id));
    setSelectedLoanIds(allSelected ? [] : allIds);
  };

  // Derived booleans used by the "Select All" checkbox to show checked / indeterminate state
  const allLoansSelected =
    overdueLoans.length > 0 &&
    overdueLoans.every((loan) => selectedLoanIds.includes(loan.loanId));
  const someLoansSelected = selectedLoanIds.length > 0 && !allLoansSelected;

  // ── Modal helpers ────────────────────────────────────────────────────────────

  // Called when manager picks a bucket — fetches overdue loans for that bucket
  const handleBucketChange = (bucket) => {
    setSelectedBucket(bucket);
    setSelectedLoanIds([]); // clear previous selections when bucket changes
    setOverdueLoans([]);

    if (!bucket) return;

    setLoansLoading(true);
    axiosInstance
      .get(`/collection-incentive/manager/overdues?bucket=${bucket}`)
      .then((res) => setOverdueLoans(res.data.data || []))
      .catch((error) => alert(error?.response?.data?.message || 'Failed to load overdue loans'))
      .finally(() => setLoansLoading(false));
  };

  // Reset all modal state and close it
  const closeModal = () => {
    setShowModal(false);
    setSelectedBucket('');
    setOverdueLoans([]);
    setSelectedLoanIds([]);
    setSelectedAgentId('');
    setAllocateReason('');
    setAllocateProgress({ done: 0, total: 0 });
  };

  // ── Final submission ─────────────────────────────────────────────────────────
  // Calls POST /allocate-manual once per selected loan (sequential, not parallel).
  // This gives the backend time to commit each allocation before the next one.
  const handleAllocate = async () => {
    if (selectedLoanIds.length === 0) return alert('Please select at least one loan');
    if (!selectedAgentId) return alert('Please select an agent');

    setAllocating(true);
    setAllocateProgress({ done: 0, total: selectedLoanIds.length });

    let successCount = 0;
    let failCount = 0;

    for (const loanId of selectedLoanIds) {
      try {
        await axiosInstance.post(
          '/collection-incentive/manager/allocate-manual',
          {
            loanId,
            targetAgentId: selectedAgentId,
            reason: allocateReason.trim() || undefined,
          }
        );
        successCount++;
      } catch {
        failCount++;
      }
      // Update the progress counter after each loan completes (success or fail)
      setAllocateProgress((prev) => ({ ...prev, done: prev.done + 1 }));
    }

    setAllocating(false);

    if (failCount === 0) {
      alert(`${successCount} loan${successCount > 1 ? 's' : ''} successfully allocated!`);
    } else {
      alert(`${successCount} allocated, ${failCount} failed. Please retry the failed ones.`);
    }

    closeModal();

    // Refresh the agent table so loan counts update immediately
    axiosInstance.get('/collection-incentive/manager/agents').then((res) => {
      setAgents(res.data.data.agents || []);
    });
  };

  if (loading) {
    return <div style={{ padding: '20px', color: '#667085' }}>Loading...</div>;
  }

  // ── Modal derived: filter → sort → paginate ──────────────────────────────────
  const MODAL_PER_PAGE = 15;
  // bucket filter is server-side; only apply search + sort client-side on the current page rows
  const modalFiltered = (() => {
    const q = modalSearch.toLowerCase();
    let rows = agentLoans.filter((loan) => {
      if (!q) return true;
      return (loan.legacyLoanId || '').toLowerCase().includes(q)
        || (loan.loanId || '').toLowerCase().includes(q)
        || (loan.borrowerName || '').toLowerCase().includes(q)
        || (loan.phone || '').includes(q);
    });
    if (modalSort.col) {
      rows = [...rows].sort((a, b) => {
        let av = a[modalSort.col], bv = b[modalSort.col];
        if (['dpd', 'totalOverdue', 'totalCollected'].includes(modalSort.col)) {
          av = Number(av) || 0; bv = Number(bv) || 0;
          return modalSort.asc ? av - bv : bv - av;
        }
        av = String(av || ''); bv = String(bv || '');
        return modalSort.asc ? av.localeCompare(bv) : bv.localeCompare(av);
      });
    }
    return rows;
  })();
  const modalTotalRows = agentLoansGrandTotals?.totalLoans ?? agentLoans.length;
  const modalTotalPages = Math.max(1, Math.ceil(modalTotalRows / MODAL_PER_PAGE));
  const modalSafePage = Math.min(modalPage, modalTotalPages);
  const modalPageSlice = modalFiltered;
  const modalTotalOv = agentLoansGrandTotals?.totalOverdue ?? agentLoans.reduce((s, l) => s + (Number(l.totalOverdue) || 0), 0);
  const modalTotalCo = agentLoansGrandTotals?.totalCollected ?? agentLoans.reduce((s, l) => s + (Number(l.totalCollected) || 0), 0);
  const modalCollPct = (modalTotalOv + modalTotalCo) > 0
    ? Math.round((modalTotalCo / (modalTotalOv + modalTotalCo)) * 100) : 0;

  // Button label changes based on how many loans are selected
  const allocateBtnLabel = allocating
    ? `Allocating ${allocateProgress.done}/${allocateProgress.total}...`
    : selectedLoanIds.length > 1
    ? `Allocate ${selectedLoanIds.length} Loans`
    : 'Allocate Loan';

  const allocateBtnDisabled = selectedLoanIds.length === 0 || !selectedAgentId || allocating;

  return (
    <div style={{ padding: '10px' }}>

      {/* ── KPI Cards ────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '18px' }}>
        <StatCard
          label="Loans in Portfolio"
          value={agents.reduce((sum, a) => sum + (a.loansAssigned || 0), 0)}
          sub="active allocations"
          icon={<AccountBalanceWalletOutlinedIcon style={{ fontSize: '24px', color: '#2563eb' }} />}
          iconBg="#eff6ff"
          cardBg="linear-gradient(to bottom left, #93c5fd 0%, #ffffff 60%)"
        />
        <StatCard
          label="Active Agents"
          value={`${agents.filter((a) => a.availability !== 'Away').length} / ${agents.length}`}
          sub={agents.length - agents.filter((a) => a.availability !== 'Away').length > 0
            ? `${agents.length - agents.filter((a) => a.availability !== 'Away').length} away`
            : 'all available'}
          icon={<GroupsOutlinedIcon style={{ fontSize: '24px', color: '#16a34a' }} />}
          iconBg="#f0fdf4"
          cardBg="linear-gradient(to bottom left, #86efac 0%, #ffffff 60%)"
        />
        <StatCard
          label="Allocation Method"
          value={allocationMethod === 'round_robin' ? 'Round Robin' : (allocationMethod || '—')}
          sub="auto-rotation cadence"
          icon={<HubOutlinedIcon style={{ fontSize: '24px', color: '#f59e0b' }} />}
          iconBg="#fffbeb"
          cardBg="linear-gradient(to bottom left, #fdd888 0%, #ffffff 60%)"
        />
        <StatCard
          label="Last Rotation"
          value={
            lastRotation?.rotationDate
              ? new Date(lastRotation.rotationDate).toLocaleDateString('en-IN', {
                  day: 'numeric',
                  month: 'short',
                })
              : '—'
          }
          sub={lastRotation ? `${lastRotation.totalLoansRotated ?? 0} loans reassigned` : 'no rotations yet'}
          icon={<AutorenewOutlinedIcon style={{ fontSize: '24px', color: '#7c3aed' }} />}
          iconBg="#f5f3ff"
          cardBg="linear-gradient(to bottom left, #d8b4fe 0%, #ffffff 60%)"
        />
      </div>

      {/* ── Allocation per Agent Table ────────────────────────────────────────── */}
      <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden', marginBottom: '18px', backgroundColor: 'white' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 12px', borderBottom: '1px solid #e0e0e0' }}>
          <p style={{ margin: 0, fontFamily: 'Inter-Medium', fontSize: '13px', fontWeight: '600', color: '#344054' }}>
            Allocation per Agent
          </p>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              onClick={handleExport}
              disabled={exporting}
              style={{
                padding: '2px 8px',
                backgroundColor: exporting ? '#f5f5f5' : 'white',
                color: exporting ? '#9e9e9e' : '#344054',
                border: '1px solid #d0d5dd',
                borderRadius: '4px',
                fontFamily: 'Inter-Medium',
                fontSize: '11px',
                fontWeight: '600',
                lineHeight: '18px',
                cursor: exporting ? 'not-allowed' : 'pointer',
              }}
            >
              {exporting ? 'Exporting...' : '↓ Export CSV'}
            </button>
            <button
              onClick={() => setShowModal(true)}
              style={{
                padding: '2px 8px',
                backgroundColor: '#d32028',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontFamily: 'Inter-Medium',
                fontSize: '11px',
                fontWeight: '600',
                lineHeight: '18px',
                cursor: 'pointer',
              }}
            >
              + Allocate
            </button>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Inter-Medium', fontSize: '12px' }}>
            <thead>
              <tr style={{ backgroundColor: '#fafbfc' }}>
                <th style={thStyle}>Agent</th>
                {BUCKET_ORDER.map((b) => (
                  <th key={b} style={thStyle}>{b}</th>
                ))}
                <th style={thStyle}>Total</th>
                <th style={{ ...thStyle, minWidth: '160px' }}></th>
              </tr>
            </thead>
            <tbody>
              {agents.length === 0 ? (
                <tr>
                  <td colSpan={BUCKET_ORDER.length + 3} style={{ padding: '24px', textAlign: 'center', color: '#667085' }}>
                    No agents in pool. Add agents via Settings tab.
                  </td>
                </tr>
              ) : (
                agents.map((agent) => (
                  <tr key={agent.agentId} style={{ borderBottom: '1px solid #e0e0e0' }}>
                    <td style={{ padding: '4px 8px', color: '#344054', fontWeight: '600' }}>
                      {agent.agentName}
                    </td>
                    {BUCKET_ORDER.map((b) => (
                      <td key={b} style={{ padding: '4px 8px', color: '#344054' }}>
                        {agent.bucketDistribution?.[b] ?? 0}
                      </td>
                    ))}
                    <td style={{ padding: '4px 8px', fontWeight: '700', color: '#344054' }}>
                      {agent.loansAssigned ?? 0}
                    </td>
                    <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          onClick={() => openAgentLoans(agent)}
                          style={{
                            padding: '2px 8px', backgroundColor: 'white',
                            color: '#344054', border: '1px solid #d0d5dd',
                            borderRadius: '4px', fontSize: '11px', fontWeight: '600',
                            lineHeight: '18px', cursor: 'pointer', whiteSpace: 'nowrap',
                          }}
                        >
                          View Loans
                        </button>
                        <button
                          onClick={() => handleExportAgent(agent)}
                          disabled={exportingAgentId === agent.agentId}
                          style={{
                            padding: '2px 8px',
                            background: exportingAgentId === agent.agentId ? '#f5f5f5' : 'linear-gradient(135deg, #d32028 0%, #ff6b6b 100%)',
                            color: exportingAgentId === agent.agentId ? '#9e9e9e' : 'white',
                            border: 'none',
                            borderRadius: '6px', fontSize: '11px', fontWeight: '600',
                            lineHeight: '18px',
                            cursor: exportingAgentId === agent.agentId ? 'not-allowed' : 'pointer',
                            whiteSpace: 'nowrap',
                            boxShadow: exportingAgentId === agent.agentId ? 'none' : '0 1px 4px rgba(211,32,40,0.3)',
                          }}
                          onMouseEnter={(e) => { if (exportingAgentId !== agent.agentId) e.currentTarget.style.background = 'linear-gradient(135deg, #b71c1c 0%, #e85555 100%)'; }}
                          onMouseLeave={(e) => { if (exportingAgentId !== agent.agentId) e.currentTarget.style.background = 'linear-gradient(135deg, #d32028 0%, #ff6b6b 100%)'; }}
                        >
                          {exportingAgentId === agent.agentId ? 'Exporting...' : '↓ Export'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Rotation History Table ────────────────────────────────────────────── */}
      <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden', backgroundColor: 'white' }}>
        <div style={{ padding: '8px 12px', borderBottom: '1px solid #e0e0e0' }}>
          <p style={{ margin: 0, fontFamily: 'Inter-Medium', fontSize: '14px', fontWeight: '600', color: '#344054' }}>
            Rotation History
          </p>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Inter-Medium', fontSize: '12px' }}>
            <thead>
              <tr style={{ backgroundColor: '#fafbfc' }}>
                {['Date', 'Type', 'Reassigned', 'PTP Held', 'By'].map((col) => (
                  <th key={col} style={thStyle}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rotationHistory.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: '24px', textAlign: 'center', color: '#667085' }}>
                    No rotation history found
                  </td>
                </tr>
              ) : (
                rotationHistory.map((record, index) => (
                  <tr key={record.id || index} style={{ borderBottom: '1px solid #e0e0e0' }}>
                    <td style={{ padding: '4px 8px', color: '#344054' }}>
                      {record.rotationDate
                        ? new Date(record.rotationDate).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                          })
                        : '—'}
                    </td>
                    <td style={{ padding: '4px 8px', color: '#344054' }}>
                      {record.triggeredBy === 'cron' ? 'Biweekly Auto' : 'Manual'}
                    </td>
                    <td style={{ padding: '4px 8px', color: '#344054' }}>{record.totalLoansRotated ?? '—'}</td>
                    <td style={{ padding: '4px 8px', color: '#344054' }}>{record.totalPtpExemptions ?? '—'}</td>
                    <td style={{ padding: '4px 8px', color: '#344054' }}>
                      {/* triggeredBy is either "cron" or "manual:<managerId>" — show a clean label */}
                      {!record.triggeredBy || record.triggeredBy === 'cron' ? 'Auto' : 'Manager'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Manual Allocation Modal ───────────────────────────────────────────── */}
      {showModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '10px',
            width: '820px',
            maxWidth: '95vw',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}>

            {/* ── Modal Header ── */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 14px', borderBottom: '1px solid #e0e0e0', flexShrink: 0,
            }}>
              <div>
                <p style={{ margin: 0, fontFamily: 'Inter-Medium', fontSize: '15px', fontWeight: '700', color: '#344054' }}>
                  Manually Allocate Loans
                </p>
                <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#667085' }}>
                  Pick a bucket → select one or more loans → assign to an agent
                </p>
              </div>
              <button
                onClick={closeModal}
                style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#667085', padding: '4px 8px' }}
              >
                ✕
              </button>
            </div>

            {/* ── Modal Body (scrollable) ── */}
            <div style={{ padding: '12px 14px', overflowY: 'auto', flex: 1 }}>

              {/* Step 1 — Bucket picker */}
              <div style={{ marginBottom: '18px' }}>
                <label style={labelStyle}>Step 1 — Select Bucket</label>
                <select
                  value={selectedBucket}
                  onChange={(e) => handleBucketChange(e.target.value)}
                  style={selectStyle}
                >
                  <option value="">Choose a bucket...</option>
                  {BUCKET_ORDER.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>

              {/* Step 2 — Loan table with multi-select */}
              {selectedBucket && (
                <div style={{ marginBottom: '18px' }}>

                  {/* Label + selection count badge */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <label style={{ ...labelStyle, marginBottom: 0 }}>
                      Step 2 — Select Loans
                      <span style={{ marginLeft: '6px', color: '#667085', fontWeight: '400' }}>
                        (tick checkbox to select, or use Select All)
                      </span>
                    </label>
                    {/* Shows "3 of 7 selected" when at least one loan is ticked */}
                    {selectedLoanIds.length > 0 && (
                      <span style={{
                        fontSize: '11px', fontWeight: '600',
                        backgroundColor: '#d32028', color: 'white',
                        padding: '3px 10px', borderRadius: '20px',
                      }}>
                        {selectedLoanIds.length} of {overdueLoans.length} selected
                      </span>
                    )}
                  </div>

                  {loansLoading ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#667085', fontSize: '13px', border: '1px solid #e0e0e0', borderRadius: '6px' }}>
                      Loading loans...
                    </div>
                  ) : overdueLoans.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#667085', fontSize: '13px', border: '1px solid #e0e0e0', borderRadius: '6px' }}>
                      No overdue loans found in bucket {selectedBucket}
                    </div>
                  ) : (
                    <div style={{ border: '1px solid #e0e0e0', borderRadius: '6px', overflow: 'hidden', maxHeight: '260px', overflowY: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', fontFamily: 'Inter-Medium' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                          <tr style={{ backgroundColor: '#f5f5f5' }}>

                            {/* Select All checkbox — checks/unchecks every row at once */}
                            <th style={{ ...modalThStyle, width: '36px', textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={allLoansSelected}
                                ref={(el) => {
                                  // "indeterminate" is a DOM property, not a React prop — must set via ref
                                  if (el) el.indeterminate = someLoansSelected;
                                }}
                                onChange={toggleSelectAll}
                                title="Select all loans"
                                style={{ cursor: 'pointer', width: '14px', height: '14px' }}
                              />
                            </th>

                            <th style={modalThStyle}>Loan ID</th>
                            <th style={modalThStyle}>Borrower</th>
                            <th style={modalThStyle}>Phone</th>
                            <th style={modalThStyle}>DPD</th>
                            <th style={modalThStyle}>Overdue ₹</th>
                            <th style={modalThStyle}>Current Agent</th>
                          </tr>
                        </thead>
                        <tbody>
                          {overdueLoans.map((loan) => {
                            const isSelected = selectedLoanIds.includes(loan.loanId);
                            return (
                              // Clicking anywhere on the row toggles its checkbox
                              <tr
                                key={loan.loanId}
                                onClick={() => toggleLoan(loan.loanId)}
                                style={{
                                  borderBottom: '1px solid #f0f0f0',
                                  cursor: 'pointer',
                                  // Highlight selected rows with a light blue-grey tint
                                  backgroundColor: isSelected ? '#f0f4ff' : 'white',
                                  transition: 'background-color 0.1s',
                                }}
                              >
                                {/* Checkbox column — clicking this also triggers the row's onClick */}
                                <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleLoan(loan.loanId)}
                                    onClick={(e) => e.stopPropagation()} // prevent double-toggle from row click
                                    style={{ cursor: 'pointer', width: '14px', height: '14px', accentColor: '#d32028' }}
                                  />
                                </td>

                                <td style={{ padding: '4px 8px', color: '#344054', fontWeight: '600' }}>
                                  {loan.legacyLoanId}
                                </td>
                                <td style={{ padding: '4px 8px', color: '#344054' }}>
                                  {loan.borrowerName || '—'}
                                </td>
                                <td style={{ padding: '4px 8px', color: '#667085' }}>
                                  {loan.phone || '—'}
                                </td>
                                <td style={{ padding: '4px 8px' }}>
                                  <span style={{
                                    display: 'inline-block',
                                    padding: '2px 8px',
                                    borderRadius: '10px',
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    backgroundColor: BUCKET_COLOR[selectedBucket]?.bg || '#f0f0f0',
                                    color: BUCKET_COLOR[selectedBucket]?.text || '#344054',
                                  }}>
                                    {loan.dpd}d
                                  </span>
                                </td>
                                <td style={{ padding: '4px 8px', color: '#d32028', fontWeight: '700', fontVariantNumeric: 'tabular-nums' }}>
                                  {formatINR(loan.totalOverdue)}
                                </td>
                                <td style={{ padding: '4px 8px', color: loan.currentAgentName ? '#344054' : '#bdbdbd', fontStyle: loan.currentAgentName ? 'normal' : 'italic' }}>
                                  {loan.currentAgentName || 'Unassigned'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Selected loans summary — shows pill for each selected loan with a remove button */}
                  {selectedLoanIds.length > 0 && (
                    <div style={{ marginTop: '10px' }}>
                      <p style={{ margin: '0 0 6px', fontSize: '11px', color: '#667085', fontWeight: '600' }}>
                        Selected loans:
                      </p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {selectedLoanIds.map((loanId) => (
                          <span
                            key={loanId}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: '4px',
                              backgroundColor: '#f0f4ff', border: '1px solid #c7d2fe',
                              borderRadius: '20px', padding: '3px 10px',
                              fontSize: '11px', color: '#3730a3', fontWeight: '600',
                            }}
                          >
                            {loanId}
                            {/* ✕ button removes this single loan from selection */}
                            <button
                              onClick={() => toggleLoan(loanId)}
                              style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                color: '#667085', fontSize: '12px', padding: '0 0 0 2px',
                                lineHeight: 1,
                              }}
                              title={`Remove ${loanId}`}
                            >
                              ✕
                            </button>
                          </span>
                        ))}
                        {/* Clear All link */}
                        <button
                          onClick={() => setSelectedLoanIds([])}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: '#667085', fontSize: '11px', textDecoration: 'underline', padding: 0,
                          }}
                        >
                          Clear all
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 3 — Agent picker */}
              <div style={{ marginBottom: '14px' }}>
                <label style={labelStyle}>Step 3 — Assign to Agent</label>
                <select
                  value={selectedAgentId}
                  onChange={(e) => setSelectedAgentId(e.target.value)}
                  style={selectStyle}
                >
                  <option value="">Choose an agent...</option>
                  {agents.map((agent) => (
                    <option key={agent.agentId} value={agent.agentId}>
                      {agent.agentName} ({agent.loansAssigned} loans)
                    </option>
                  ))}
                </select>
              </div>

              {/* Step 4 — Optional reason */}
              <div style={{ marginBottom: '8px' }}>
                <label style={labelStyle}>
                  Reason <span style={{ color: '#9e9e9e', fontWeight: '400' }}>(optional)</span>
                </label>
                <input
                  type="text"
                  value={allocateReason}
                  onChange={(e) => setAllocateReason(e.target.value)}
                  placeholder="e.g. Specialist in long-overdue loans"
                  style={selectStyle}
                />
              </div>

            </div>

            {/* ── Modal Footer ── */}
            <div style={{
              display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'flex-end',
              padding: '10px 14px', borderTop: '1px solid #e0e0e0', flexShrink: 0,
            }}>

              {/* Progress text shown while allocating multiple loans */}
              {allocating && (
                <span style={{ fontSize: '12px', color: '#667085', marginRight: 'auto' }}>
                  Allocating {allocateProgress.done} of {allocateProgress.total} loans...
                </span>
              )}

              <button
                onClick={closeModal}
                disabled={allocating}
                style={{
                  padding: '5px 14px',
                  backgroundColor: 'white', color: '#344054',
                  border: '1px solid #d0d5dd', borderRadius: '6px',
                  fontFamily: 'Inter-Medium', fontSize: '12px',
                  cursor: allocating ? 'not-allowed' : 'pointer',
                  opacity: allocating ? 0.5 : 1,
                }}
              >
                Cancel
              </button>

              <button
                onClick={handleAllocate}
                disabled={allocateBtnDisabled}
                style={{
                  padding: '5px 18px',
                  backgroundColor: allocateBtnDisabled ? '#e0e0e0' : '#d32028',
                  color: allocateBtnDisabled ? '#9e9e9e' : 'white',
                  border: 'none', borderRadius: '6px',
                  fontFamily: 'Inter-Medium', fontSize: '12px', fontWeight: '600',
                  cursor: allocateBtnDisabled ? 'not-allowed' : 'pointer',
                  minWidth: '140px',
                }}
              >
                {allocateBtnLabel}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ── Agent Loans Modal — Premium Fintech Design ──────────────────────────── */}
      {peekAgent && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: '16px',
        }}>
          <div style={{
            backgroundColor: '#FFFFFF', borderRadius: '10px',
            width: '100%', maxWidth: '1100px', maxHeight: '92vh',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
            overflow: 'hidden',
          }}>

            {/* ── Header ── */}
            <div style={{
              background: 'white', borderBottom: '1px solid #e0e0e0',
              padding: '10px 16px', flexShrink: 0,
              display: 'flex', alignItems: 'center', gap: '10px',
            }}>
              <div style={{
                width: '34px', height: '34px', borderRadius: '8px',
                background: 'linear-gradient(135deg, #111827 0%, #374151 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'white', fontSize: '14px', fontWeight: 700, flexShrink: 0,
              }}>
                {peekAgent.agentName?.[0]?.toUpperCase()}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '14px', fontWeight: 700, color: '#111827' }}>
                  {peekAgent.agentName}
                </span>
                {!agentLoansLoading && (
                  <span style={{
                    padding: '1px 8px', borderRadius: '20px',
                    background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb',
                    fontSize: '11px', fontWeight: 600,
                  }}>
                    {agentLoansGrandTotals?.totalLoans ?? agentLoans.length} Active Loans
                  </span>
                )}
                {!agentLoansLoading && modalCollPct > 0 && (
                  <span style={{
                    padding: '1px 8px', borderRadius: '20px',
                    background: modalCollPct >= 50 ? '#F0FDF4' : '#FFFBEB',
                    color: modalCollPct >= 50 ? '#16A34A' : '#D97706',
                    border: `1px solid ${modalCollPct >= 50 ? '#BBF7D0' : '#FDE68A'}`,
                    fontSize: '11px', fontWeight: 700,
                  }}>
                    {modalCollPct}% Recovery
                  </span>
                )}
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <button
                  onClick={closeAgentLoans}
                  style={{
                    width: '28px', height: '28px', borderRadius: '7px',
                    background: 'white', border: '1px solid #E5E7EB',
                    color: '#6B7280', cursor: 'pointer', fontSize: '16px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.15s ease', flexShrink: 0,
                  }}
                >✕</button>
              </div>
            </div>

            {/* ── KPI Strip ── */}
            {!agentLoansLoading && agentLoans.length > 0 && (
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(4,1fr)',
                borderBottom: '1px solid #e0e0e0', flexShrink: 0,
                background: '#fafbfc',
              }}>
                {[
                  { label: 'Total Loans', value: String(agentLoansGrandTotals?.totalLoans ?? agentLoans.length), color: '#111827' },
                  { label: 'Total Overdue', value: formatINR(modalTotalOv), color: '#d32028' },
                  { label: 'Total Collected', value: formatINR(modalTotalCo), color: modalTotalCo > 0 ? '#16A34A' : '#9CA3AF' },
                  { label: 'Collection Rate', value: `${modalCollPct}%`, color: modalCollPct >= 50 ? '#16A34A' : modalCollPct > 0 ? '#D97706' : '#9CA3AF' },
                ].map((stat, i) => (
                  <div key={i} style={{
                    padding: '8px 14px',
                    borderRight: i < 3 ? '1px solid #e0e0e0' : 'none',
                  }}>
                    <div style={{ fontSize: '10px', color: '#667085', fontWeight: 600, marginBottom: '2px' }}>
                      {stat.label}
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: stat.color, fontVariantNumeric: 'tabular-nums' }}>
                      {stat.value}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── Toolbar ── */}
            {!agentLoansLoading && agentLoans.length > 0 && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '6px 14px', borderBottom: '1px solid #e0e0e0',
                flexWrap: 'wrap', flexShrink: 0,
              }}>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {['ALL', ...BUCKET_ORDER].map((b) => {
                    const active = modalBucket === b;
                    const bc = b !== 'ALL' ? BUCKET_COLOR[b] : null;
                    const cnt = b === 'ALL'
                      ? (agentLoansGrandTotals?.totalLoans ?? agentLoans.length)
                      : (agentBucketCounts[b] ?? 0);
                    return (
                      <button
                        key={b}
                        onClick={() => { setModalBucket(b); setModalPage(1); fetchModalLoans(peekAgent.agentId, 1, b); }}
                        style={{
                          padding: '0 8px', borderRadius: '4px', cursor: 'pointer',
                          lineHeight: '20px', height: '20px',
                          border: active
                            ? (bc ? `1px solid ${bc.border}` : '1px solid #d32028')
                            : '1px solid #e0e0e0',
                          background: active ? (bc ? bc.bg : '#FFF5F5') : 'white',
                          color: active ? (bc ? bc.text : '#d32028') : '#667085',
                          fontSize: '11px', fontWeight: active ? 700 : 500,
                          display: 'inline-flex', alignItems: 'center', gap: '4px',
                        }}
                      >
                        {b === 'ALL' ? 'All' : b}
                        <span style={{
                          fontSize: '10px', fontWeight: 700, lineHeight: '14px',
                          padding: '0 4px', borderRadius: '8px',
                          background: active ? (bc ? bc.text : '#d32028') : '#e0e0e0',
                          color: 'white',
                        }}>{cnt}</span>
                      </button>
                    );
                  })}
                </div>
                <div style={{ marginLeft: 'auto', position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <span style={{ position: 'absolute', left: '8px', color: '#9CA3AF', fontSize: '13px', pointerEvents: 'none' }}>⌕</span>
                  <input
                    type="text"
                    value={modalSearch}
                    onChange={(e) => { setModalSearch(e.target.value); setModalPage(1); }}
                    placeholder="Search loan ID, borrower…"
                    style={{
                      paddingLeft: '26px', paddingRight: '10px', paddingTop: '4px', paddingBottom: '4px',
                      border: '1px solid #e0e0e0', borderRadius: '6px',
                      fontSize: '12px', color: '#344054', width: '210px',
                      background: 'white', outline: 'none',
                    }}
                  />
                </div>
              </div>
            )}

            {/* ── Table ── */}
            <div style={{ overflowY: 'auto', flex: 1, overflowX: 'auto' }}>
              {agentLoansLoading ? (
                <div style={{ padding: '64px', textAlign: 'center', color: '#9CA3AF', fontSize: '13px' }}>
                  Loading loans…
                </div>
              ) : agentLoans.length === 0 ? (
                <div style={{ padding: '64px', textAlign: 'center' }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#6B7280' }}>No active loans assigned to this agent</div>
                </div>
              ) : modalFiltered.length === 0 ? (
                <div style={{ padding: '64px', textAlign: 'center' }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#6B7280' }}>No loans match the current filters</div>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {[
                        { label: 'Loan ID',      col: 'legacyLoanId',   right: false },
                        { label: 'Bucket',        col: 'bucket',         right: false },
                        { label: 'DPD',           col: 'dpd',            right: false },
                        { label: 'Overdue ₹',     col: 'totalOverdue',   right: true  },
                        { label: 'Collected ₹',   col: 'totalCollected', right: true  },
                        { label: 'Recovery',      col: null,             right: false },
                        { label: 'Allocated',     col: 'allocatedAt',    right: false },
                        { label: '',              col: null,             right: false },
                      ].map((h, idx) => (
                        <th
                          key={idx}
                          onClick={() => h.col && (
                            setModalSort((prev) => prev.col === h.col ? { col: h.col, asc: !prev.asc } : { col: h.col, asc: true }),
                            setModalPage(1)
                          )}
                          style={{
                            padding: '5px 10px',
                            fontSize: '10px', fontWeight: 700, letterSpacing: '0.07em',
                            textTransform: 'uppercase', whiteSpace: 'nowrap',
                            backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0',
                            cursor: h.col ? 'pointer' : 'default', userSelect: 'none',
                            textAlign: h.right ? 'right' : 'left',
                            position: 'sticky', top: 0, zIndex: 2,
                            color: modalSort.col === h.col ? '#d32028' : '#667085',
                          }}
                        >
                          {h.label}{h.col && modalSort.col === h.col ? (modalSort.asc ? ' ↑' : ' ↓') : ''}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {modalPageSlice.map((loan, i) => {
                      const bc = BUCKET_COLOR[loan.bucket] || { bg: '#F3F4F6', text: '#6B7280', border: '#E5E7EB' };
                      const overdue = Number(loan.totalOverdue) || 0;
                      const collected = Number(loan.totalCollected) || 0;
                      const base = overdue + collected;
                      const pct = overdue > 0 ? Math.round((collected / base) * 100) : 0;
                      const dpd = Number(loan.dpd) || 0;
                      const dpdBg = dpd <= 30 ? '#BBF7D0' : dpd <= 90 ? '#FDE68A' : '#FCA5A5';
                      const dpdFg = dpd <= 30 ? '#14532D' : dpd <= 90 ? '#78350F' : '#7F1D1D';
                      const recColor = pct >= 75 ? '#16A34A' : pct >= 40 ? '#D97706' : '#DC2626';
                      const isReassigning = reassignLoanId === loan.loanId;

                      return (
                        <React.Fragment key={loan.loanId || i}>
                          <tr
                            style={{
                              borderBottom: isReassigning ? 'none' : '1px solid #F3F4F6',
                              backgroundColor: isReassigning ? '#FFF5F5' : 'white',
                              borderLeft: isReassigning ? '3px solid #d32028' : '3px solid transparent',
                              transition: 'background 0.1s',
                            }}
                            onMouseEnter={(e) => { if (!isReassigning) e.currentTarget.style.backgroundColor = '#F8FAFC'; }}
                            onMouseLeave={(e) => { if (!isReassigning) e.currentTarget.style.backgroundColor = 'white'; }}
                          >
                            {/* Loan ID */}
                            <td style={{ padding: '4px 10px', whiteSpace: 'nowrap' }}>
                              <span style={{ fontSize: '12px', fontWeight: 600, color: '#344054' }}>
                                {loan.legacyLoanId || loan.loanId || '—'}
                              </span>
                            </td>
                            {/* Bucket */}
                            <td style={{ padding: '4px 10px' }}>
                              <span style={{
                                display: 'inline-block',
                                padding: '0 5px', borderRadius: '3px',
                                fontSize: '10px', fontWeight: 700, lineHeight: '16px',
                                background: bc.bg, color: bc.text,
                                border: `1px solid ${bc.border || bc.bg}`,
                              }}>
                                {loan.bucket || '—'}
                              </span>
                            </td>
                            {/* DPD */}
                            <td style={{ padding: '4px 10px' }}>
                              <span style={{
                                display: 'inline-block',
                                padding: '0 5px', borderRadius: '3px',
                                fontSize: '10px', fontWeight: 700, lineHeight: '16px',
                                fontVariantNumeric: 'tabular-nums',
                                background: dpdBg, color: dpdFg,
                              }}>
                                {loan.dpd != null ? `${dpd}d` : '—'}
                              </span>
                            </td>
                            {/* Overdue */}
                            <td style={{ padding: '4px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                              {overdue > 0
                                ? <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: '#d32028', fontSize: '12px' }}>{formatINR(overdue)}</span>
                                : <span style={{ color: '#bdbdbd' }}>—</span>}
                            </td>
                            {/* Collected */}
                            <td style={{ padding: '4px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                              {collected > 0
                                ? <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: '#16A34A', fontSize: '12px' }}>{formatINR(collected)}</span>
                                : <span style={{ color: '#bdbdbd' }}>—</span>}
                            </td>
                            {/* Recovery bar */}
                            <td style={{ padding: '4px 10px' }}>
                              {base > 0 ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: '80px' }}>
                                  <div style={{ flex: 1, height: '4px', background: '#e0e0e0', borderRadius: '2px', overflow: 'hidden' }}>
                                    <div style={{ height: '100%', borderRadius: '2px', background: recColor, width: `${Math.min(pct, 100)}%` }} />
                                  </div>
                                  <span style={{ fontSize: '11px', fontWeight: 600, color: '#667085', minWidth: '26px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                    {pct}%
                                  </span>
                                </div>
                              ) : (
                                <span style={{ color: '#bdbdbd', fontSize: '12px' }}>—</span>
                              )}
                            </td>
                            {/* Date */}
                            <td style={{ padding: '4px 10px', whiteSpace: 'nowrap' }}>
                              <span style={{ fontSize: '11px', color: '#667085' }}>
                                {loan.allocatedAt ? new Date(loan.allocatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                              </span>
                            </td>
                            {/* Reassign button */}
                            <td style={{ padding: '4px 10px' }}>
                              <button
                                onClick={() => {
                                  if (isReassigning) { setReassignLoanId(null); setReassignTargetId(''); setReassignReason(''); }
                                  else { setReassignLoanId(loan.loanId); setReassignTargetId(''); setReassignReason(''); }
                                }}
                                style={{
                                  padding: '2px 8px', borderRadius: '4px', whiteSpace: 'nowrap',
                                  border: `1px solid ${isReassigning ? '#FECACA' : '#d0d5dd'}`,
                                  background: isReassigning ? '#FFF5F5' : 'white',
                                  color: isReassigning ? '#d32028' : '#344054',
                                  fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                                  lineHeight: '18px',
                                }}
                              >
                                {isReassigning ? 'Cancel' : 'Reassign'}
                              </button>
                            </td>
                          </tr>

                          {/* Inline reassign expansion row */}
                          {isReassigning && (
                            <tr>
                              <td colSpan={8} style={{
                                background: 'white',
                                borderBottom: '1px solid #F3F4F6',
                                padding: '10px 12px 12px 32px',
                              }}>
                                <div style={{
                                  background: '#FEE2E2',
                                  border: '1px solid #FECACA',
                                  borderLeft: '3px solid #d32028',
                                  borderRadius: '6px',
                                  padding: '7px 12px',
                                  marginRight: '40px',
                                }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                  <span style={{ fontSize: '10px', fontWeight: 700, color: '#d32028', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                                    Reassign {loan.legacyLoanId || loan.loanId}
                                  </span>
                                  <select
                                    value={reassignTargetId}
                                    onChange={(e) => setReassignTargetId(e.target.value)}
                                    style={{ background: 'white', border: '1px solid #FECACA', borderRadius: '4px', padding: '0 8px', fontSize: '12px', color: '#344054', outline: 'none', width: '200px', height: '28px', boxSizing: 'border-box', flexShrink: 0 }}
                                  >
                                    <option value="">Move to agent…</option>
                                    {agents.filter((a) => a.agentId !== peekAgent.agentId).map((a) => (
                                      <option key={a.agentId} value={a.agentId}>{a.agentName} ({a.loansAssigned} loans)</option>
                                    ))}
                                  </select>
                                  <input
                                    type="text"
                                    value={reassignReason}
                                    onChange={(e) => setReassignReason(e.target.value)}
                                    placeholder="Reason (optional)"
                                    style={{ background: 'white', border: '1px solid #FECACA', borderRadius: '4px', padding: '0 8px', fontSize: '12px', color: '#344054', outline: 'none', flex: 1, height: '28px', boxSizing: 'border-box' }}
                                  />
                                  <button
                                    onClick={handleReassign}
                                    disabled={!reassignTargetId || reassigning}
                                    style={{
                                      height: '28px', padding: '0 14px', boxSizing: 'border-box',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      background: !reassignTargetId || reassigning ? '#e0e0e0' : '#d32028',
                                      color: !reassignTargetId || reassigning ? '#9e9e9e' : 'white',
                                      border: 'none', borderRadius: '4px',
                                      fontSize: '11px', fontWeight: 600,
                                      cursor: !reassignTargetId || reassigning ? 'not-allowed' : 'pointer',
                                      whiteSpace: 'nowrap', flexShrink: 0,
                                    }}
                                  >
                                    {reassigning ? 'Moving…' : 'Confirm'}
                                  </button>
                                  <button
                                    onClick={() => { setReassignLoanId(null); setReassignTargetId(''); setReassignReason(''); }}
                                    style={{ height: '28px', padding: '0 12px', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white', border: '1px solid #d0d5dd', color: '#667085', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', flexShrink: 0 }}
                                  >Cancel</button>
                                </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* ── Pagination footer ── */}
            {!agentLoansLoading && modalFiltered.length > 0 && (
              <div style={{
                borderTop: '1px solid #e0e0e0', padding: '6px 14px',
                display: 'flex', alignItems: 'center', gap: '4px',
                flexShrink: 0, background: '#fafbfc',
              }}>
                <span style={{ fontSize: '11px', color: '#667085', flex: 1, fontVariantNumeric: 'tabular-nums' }}>
                  {`${(modalSafePage - 1) * MODAL_PER_PAGE + 1}–${(modalSafePage - 1) * MODAL_PER_PAGE + modalFiltered.length} of ${modalTotalRows} loans`}
                </span>
                <button
                  disabled={modalSafePage <= 1}
                  onClick={() => { const pg = Math.max(1, modalSafePage - 1); setModalPage(pg); fetchModalLoans(peekAgent.agentId, pg, modalBucket); }}
                  style={{
                    padding: '2px 10px', border: '1px solid #d0d5dd', background: 'white',
                    color: '#344054', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                    lineHeight: '18px',
                    cursor: modalSafePage <= 1 ? 'not-allowed' : 'pointer',
                    opacity: modalSafePage <= 1 ? 0.35 : 1,
                  }}
                >← Prev</button>
                <div style={{ display: 'flex', gap: '2px' }}>
                  {Array.from({ length: Math.min(modalTotalPages, 5) }, (_, i) => {
                    const lo = Math.max(1, modalSafePage - 2);
                    const pg = lo + i;
                    if (pg > modalTotalPages) return null;
                    return (
                      <button
                        key={pg}
                        onClick={() => { setModalPage(pg); fetchModalLoans(peekAgent.agentId, pg, modalBucket); }}
                        style={{
                          width: '26px', height: '26px',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          borderRadius: '4px', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                          border: `1px solid ${pg === modalSafePage ? '#d32028' : 'transparent'}`,
                          background: pg === modalSafePage ? '#d32028' : 'none',
                          color: pg === modalSafePage ? 'white' : '#667085',
                        }}
                      >{pg}</button>
                    );
                  })}
                </div>
                <button
                  disabled={modalSafePage >= modalTotalPages}
                  onClick={() => { const pg = Math.min(modalTotalPages, modalSafePage + 1); setModalPage(pg); fetchModalLoans(peekAgent.agentId, pg, modalBucket); }}
                  style={{
                    padding: '2px 10px', border: '1px solid #d0d5dd', background: 'white',
                    color: '#344054', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                    lineHeight: '18px',
                    cursor: modalSafePage >= modalTotalPages ? 'not-allowed' : 'pointer',
                    opacity: modalSafePage >= modalTotalPages ? 0.35 : 1,
                  }}
                >Next →</button>
              </div>
            )}

          </div>
        </div>
      )}

    </div>
  );
}

function StatCard({ label, value, sub, icon, iconBg, cardBg }) {
  return (
    <div style={{
      flex: 1,
      background: cardBg,
      border: '1px solid #f0f0f0',
      borderRadius: '12px',
      padding: '14px 16px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
    }}>
      <div>
        <p style={{ margin: 0, fontSize: '11px', color: '#667085', fontWeight: '600', fontFamily: 'Inter-Medium' }}>
          {label}
        </p>
        <p style={{ margin: '4px 0 2px', fontSize: '20px', fontWeight: '700', color: '#101828', lineHeight: 1.2, fontFamily: 'Inter-Medium' }}>
          {value}
        </p>
        {sub && (
          <p style={{ margin: 0, fontSize: '10px', color: '#9e9e9e', fontFamily: 'Inter-Medium' }}>{sub}</p>
        )}
      </div>
      <div style={{
        width: '44px', height: '44px', borderRadius: '10px', flexShrink: 0,
        backgroundColor: iconBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {icon}
      </div>
    </div>
  );
}

// ── Style constants ──────────────────────────────────────────────────────────

const thStyle = {
  padding: '4px 8px',
  textAlign: 'left',
  color: '#667085',
  fontWeight: '600',
  borderBottom: '1px solid #e0e0e0',
  fontSize: '11px',
  whiteSpace: 'nowrap',
};

const modalThStyle = {
  padding: '4px 8px',
  textAlign: 'left',
  color: '#667085',
  fontWeight: '600',
  borderBottom: '1px solid #e0e0e0',
  fontSize: '11px',
  whiteSpace: 'nowrap',
  backgroundColor: '#f5f5f5',
};

const labelStyle = {
  display: 'block',
  fontSize: '12px',
  fontWeight: '600',
  color: '#344054',
  marginBottom: '6px',
};

const selectStyle = {
  width: '100%',
  padding: '4px 8px',
  border: '1px solid #d0d5dd',
  borderRadius: '6px',
  fontSize: '13px',
  backgroundColor: 'white',
  boxSizing: 'border-box',
  color: '#344054',
};
